import * as React from 'react';

import { cn } from './lib/cn';

/*
 * Avatar — deterministic 8-hue rotation from hash(entityId) % 8 (tokens.md §2.1).
 * Pass a stable seed (user id / email) to get a consistent color across renders.
 */

const PALETTE_SIZE = 8;

function hashSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % PALETTE_SIZE;
}

const paletteClass: Record<number, string> = {
  0: 'bg-avatar-1',
  1: 'bg-avatar-2',
  2: 'bg-avatar-3',
  3: 'bg-avatar-4',
  4: 'bg-avatar-5',
  5: 'bg-avatar-6',
  6: 'bg-avatar-7',
  7: 'bg-avatar-8',
};

interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  initials: string;
  seed?: string;
  size?: 'sm' | 'md' | 'lg';
  src?: string | undefined;
}

const sizeClass: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-7 w-7 text-sm',
  lg: 'h-10 w-10 text-base',
};

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, initials, seed, size = 'md', src, ...props }, ref) => {
    const bucket = seed !== undefined ? hashSeed(seed) : 0;
    const [imgOk, setImgOk] = React.useState(src !== undefined);

    React.useEffect(() => {
      setImgOk(src !== undefined);
    }, [src]);

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/[0.08] font-semibold text-text-secondary',
          paletteClass[bucket],
          sizeClass[size],
          className,
        )}
        {...props}
      >
        {src !== undefined && imgOk ? (
          <img src={src} alt="" aria-hidden className="h-full w-full object-cover" onError={() => setImgOk(false)} />
        ) : (
          initials
        )}
      </span>
    );
  },
);
Avatar.displayName = 'Avatar';

export { Avatar };
